import React, { useState } from "react";
import { CheckCircle2, Circle, ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";
import IdentityVerify from "@/components/IdentityVerify";
import SelfieCapture from "@/components/SelfieCapture";
import { AddVehicle, TypePicker, RcBadge, VEHICLE_LABELS, SELF_DRIVEN } from "@/components/VehicleManager";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const CARRIER_SETUP_MSG = "Complete your verification to start receiving parcel requests.";

const Step = ({ n, title, done, open, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
    <div className="flex items-center gap-2">
      {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <Circle className="w-4 h-4 text-slate-300"/>}
      <span className="text-sm font-semibold">{n}. {title}</span>
      {done && <span className="text-[11px] text-emerald-700">Done</span>}
    </div>
    {open && !done && <div className="mt-3 pl-6">{children}</div>}
  </div>
);

const Transport = () => {
  const { user, refresh } = useAuth();
  const [type, setType] = useState(null);
  const pending = (user?.vehicles || []).filter((v) => SELF_DRIVEN.has(v.type) && v.rc_status !== "verified");
  const verify = async (v) => {
    try { await api.post(`/vehicles/${v.id}/verify-rc`); await refresh(); } catch (e) { toast.error(apiError(e, "RC check failed")); }
  };
  return (
    <div className="space-y-3">
      {pending.map((v) => (
        <div key={v.id} className="flex items-center gap-2 flex-wrap text-sm">
          <span>{VEHICLE_LABELS[v.type]} · <span className="font-mono">{v.reg_no}</span></span><RcBadge v={v}/>
          {["unverified", "failed"].includes(v.rc_status) && <Button size="sm" className="h-7 rounded-full" data-testid={`setup-verify-rc-${v.id}`} onClick={() => verify(v)}>Verify RC</Button>}
        </div>
      ))}
      <TypePicker value={type} onChange={setType}/>
      {type && <AddVehicle type={type} onAdded={async () => { await refresh(); setType(null); }}/>}
    </div>
  );
};

// Carrier onboarding, in order: identity → selfie → transport (vehicle + RC for own vehicles). Bank is never asked here.
export const CarrierSetup = ({ missing: missingProp, compact = false }) => {
  const { user } = useAuth();
  const missing = missingProp || user?.carrier_missing || [];
  if (!missing.length) return null;
  const idDone = user?.kyc_status === "approved";
  const idWaiting = !idDone && !!user?.kyc_doc_id;
  const selfieDone = !!user?.selfie_file_id;
  const transportDone = !missing.includes("transport") && !missing.includes("rc");
  return (
    <div className={`rounded-2xl border border-teal-200 bg-teal-50 ${compact ? "p-3" : "p-4"} space-y-3`} data-testid="carrier-setup">
      <div className="flex gap-2 text-teal-900">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5"/>
        <div>
          <div className="font-semibold" data-testid="carrier-setup-msg">{CARRIER_SETUP_MSG}</div>
          <div className="text-xs text-teal-800/80 mt-0.5">You can post trips and browse parcels meanwhile. Senders only see you once this is done.</div>
        </div>
      </div>
      <Step n={1} title="Verify your identity (Aadhaar)" done={idDone} open testid="setup-step-identity"><IdentityVerify/></Step>
      <Step n={2} title="Add a profile selfie" done={selfieDone} open={idDone || idWaiting} testid="setup-step-selfie"><SelfieCapture/></Step>
      <Step n={3} title="How do you travel?" done={transportDone} open={(idDone || idWaiting) && selfieDone} testid="setup-step-transport"><Transport/></Step>
    </div>
  );
};

export default CarrierSetup;
